import { useState } from 'react';
import { KML, GeoJSON as GeoJSONFormat } from 'ol/format';
import { Style, Stroke, Fill } from 'ol/style';

import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import { createFeature } from '@utils/map/helpers';
import { downloadFile } from '@utils/files';
import { useCollectionStore } from '@stores/useCollectionStore';
import { hexToRgba } from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';
import {
	captureMapCanvas,
	captureMapCanvasAsBuffer,
	generatePDFContent,
	exportToDOCX,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/utils';
import { prepareCollectionExportData } from '../utils/collectionExportData';

// Fill opacity for hexagons with no assigned class (heat mode / unclassified).
// The hue follows the user's chosen layer color so the export matches the map;
// only this flat alpha differs, since a static file can't carry the map's
// density ramp.
const HEAT_FALLBACK_ALPHA = 0.4;

/**
 * PDF/DOCX report export and KML/GeoJSON geometry export for the hexagon
 * color-filter. Isolated from template/classification concerns — this hook
 * only ever *reads* the resolved classification (`results`/`classByCellId`),
 * it never computes or mutates it.
 *
 * @param {object} params
 * @param {import('../utils/collectionHexagonsToCells').HexagonCell[]} params.cells
 * @param {Record<string, string>} params.classByCellId
 * @param {object} params.results - per-class classification results
 * @param {object|null} params.filter - active filter config
 * @param {string} params.collectionName
 * @param {Array<{key: string, label: string}>} params.objects - collection detection objects
 * @param {number} params.resolution - hexagon cell size (m²)
 * @param {string} params.projectNotes
 * @param {{id,symbol,label}} params.currency
 * @param {Array<{id,label,unit}>} params.availableProperties
 * @param {object|null} params.coverageGaps
 * @param {object} params.summary - whole-collection detection summary
 * @returns {{
 *   showExportModal: boolean, setShowExportModal: Function,
 *   exportTheme: string, setExportTheme: Function,
 *   isExporting: boolean,
 *   handleExport: (format: 'pdf'|'docx') => void,
 *   exportGeometry: (type: 'kml'|'geojson') => void,
 * }}
 */
const useHexagonExport = ({
	cells,
	classByCellId,
	results,
	filter,
	collectionName,
	objects,
	resolution,
	projectNotes,
	currency,
	availableProperties,
	coverageGaps,
	summary,
}) => {
	const { mapObject, defaultProjection, project } = useProject();
	// Heat-mode hue, for cells the active filter leaves unclassified.
	const hexagonColor = useCollectionStore(s => s.hexagonColor);
	const { currentUser } = useAuth();
	const { addToast } = useToast();

	const [showExportModal, setShowExportModal] = useState(false);
	const [exportTheme, setExportTheme] = useState('dark');
	const [isExporting, setIsExporting] = useState(false);

	// Shared export data builder — collection-native counterpart to the model
	// path's prepareExportData (see collectionExportData.js for why this is a
	// separate adapter rather than the same function).
	const buildExportData = () =>
		prepareCollectionExportData({
			filter,
			classificationResults: results,
			collectionName,
			project,
			objects,
			hexagonSize: resolution,
			userName: currentUser?.userName || currentUser?.email,
			projectNotes,
			currency,
			availableProperties,
			coverageGaps,
			summary,
		});

	const exportPDF = theme => {
		setIsExporting(true);
		try {
			const mapImageData = captureMapCanvas(mapObject);
			const exportData = buildExportData();
			const htmlContent = generatePDFContent(exportData, theme, mapImageData);

			const printWindow = window.open('', '_blank');
			if (!printWindow) {
				addToast({ title: 'Please allow popups for this site', bg: 'warning' });
				return;
			}
			printWindow.document.write(htmlContent);
			printWindow.document.close();
			addToast({ title: 'Report opened in new tab', bg: 'success' });
		} catch (err) {
			console.error('PDF export error:', err);
			addToast({ title: 'Failed to generate report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	const exportDOCX = async () => {
		setIsExporting(true);
		try {
			const mapImageBuffer = captureMapCanvasAsBuffer(mapObject);
			const exportData = buildExportData();
			await exportToDOCX(exportData, mapImageBuffer);
			addToast({ title: 'DOCX report downloaded', bg: 'success' });
		} catch (err) {
			console.error('DOCX export error:', err);
			addToast({ title: 'Failed to generate DOCX report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	const handleExport = format => {
		setShowExportModal(false);
		if (format === 'pdf') exportPDF(exportTheme);
		else if (format === 'docx') exportDOCX();
	};

	// KML/GeoJSON export — collection-native counterpart to the model path's
	// HexagonColorFilterLayer.handleExport. That version reads OL features
	// already on the map; here the cells are plain data, so we build one-off OL
	// features styled by class (or the heat fallback) purely for the export.
	const exportGeometry = type => {
		try {
			const format = type === 'kml' ? new KML() : new GeoJSONFormat();
			const fileExtension = type === 'kml' ? 'kml' : 'geojson';
			const fileType = type === 'kml' ? 'vnd.google-earth.kml+xml' : 'geo+json';

			const features = cells
				.map(cell => {
					const classResult = results[classByCellId[cell.id]];
					const feature = createFeature(cell.geometry, {
						name: classResult?.label || 'Unclassified',
					});
					if (!feature) return null;

					const rgba = hexToRgba(
						classResult ? classResult.color : hexagonColor,
						classResult ? classResult.opacity : HEAT_FALLBACK_ALPHA
					);
					feature.setStyle(
						new Style({
							stroke: new Stroke({ color: '#000', width: 1 }),
							fill: new Fill({
								color: `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
							}),
						})
					);
					return feature;
				})
				.filter(Boolean);

			const data = format.writeFeatures(features, { featureProjection: defaultProjection });
			const blob = new Blob([data], { type: `application/${fileType}` });
			const url = URL.createObjectURL(blob);
			downloadFile({ url, title: collectionName || 'hexagons', fileExtension });
			// Revoked on a delay rather than immediately after triggering the
			// download — some browsers process the anchor's click() asynchronously,
			// and an immediate revoke can cancel the in-flight download.
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch (err) {
			console.error(`${type} export error:`, err);
			addToast({ title: 'Failed to export geometry', bg: 'danger' });
		}
	};

	return {
		showExportModal,
		setShowExportModal,
		exportTheme,
		setExportTheme,
		isExporting,
		handleExport,
		exportGeometry,
	};
};

export default useHexagonExport;
